import { useEffect, useRef, useState } from "react";
import { MessageSquare, Check, Laptop, Settings2, Loader2, UserPlus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const fmtEta = (sec) => {
  if (sec == null || !isFinite(sec) || sec <= 0) return "estimating time…";
  if (sec < 45) return "under a minute left";
  const m = Math.round(sec / 60);
  return `about ${m} min left`;
};

// iMessage-style green chat bubble.
function IMessageLogo({ size = 17, className = "" }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} aria-hidden="true">
      <path fill="#34C759" d="M12 3C6.9 3 3 6.52 3 10.8c0 2.3 1.12 4.33 2.9 5.73-.19 1.2-.78 2.3-1.67 3.17-.23.22-.04.6.28.56 1.9-.22 3.4-.9 4.42-1.59 1 .3 2.06.46 3.07.46 5.1 0 9-3.52 9-7.8S17.1 3 12 3z"/>
    </svg>
  );
}

export function NotificationBell({ onOpen }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [conn, setConn] = useState(null);
  const [open, setOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [eta, setEta] = useState(null);
  const seen = useRef(null);
  const prevCount = useRef(null);
  const importingRef = useRef(false);
  const stableTicks = useRef(0);
  const rateRef = useRef(0);
  const lastTs = useRef(0);

  const load = async () => {
    let status = null;
    try { status = await api.imessageStatus(); setConn(status); } catch {}
    // Detect a big incoming batch (first live sync pulls in a ton of history).
    const count = typeof status?.messages === "number" ? status.messages : null;
    const now = Date.now();
    let bulk = importingRef.current;
    if (count != null && prevCount.current != null) {
      const delta = count - prevCount.current;
      if (delta > 0 && lastTs.current) {
        const inst = delta / Math.max(1, (now - lastTs.current) / 1000);
        rateRef.current = rateRef.current ? rateRef.current * 0.5 + inst * 0.5 : inst;
      }
      if (delta >= 25) { bulk = true; stableTicks.current = 0; }
      else if (bulk) { stableTicks.current += 1; if (stableTicks.current >= 2) bulk = false; }
    }
    if (count != null) { prevCount.current = count; lastTs.current = now; }
    importingRef.current = bulk;
    setImporting(bulk);
    // Estimate remaining time from the device's total vs. what we've stored.
    if (bulk && count != null && status?.total_expected > count && rateRef.current > 0) {
      setEta((status.total_expected - count) / rateRef.current);
    } else if (!bulk) { setEta(null); }

    try {
      const r = await api.imessageNotifications();
      const list = r.notifications || [];
      // Only pop toasts for a trickle of new messages. More than 5 at once (a burst) → stay silent,
      // they still land in the bell. Also silent during a big import.
      if (seen.current && !bulk) {
        const fresh = list.filter((n) => !n.read && !seen.current.has(n.id));
        if (fresh.length > 0 && fresh.length <= 5) {
          fresh.slice(0, 3).forEach((n) =>
            toast(`iMessage · ${n.name}`, { description: n.preview, icon: "💬", duration: 6000 }));
        }
      }
      seen.current = new Set(list.map((n) => n.id));
      setItems(list);
      setUnread(r.unread || 0);
    } catch { /* not connected yet */ }
  };

  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

  const markAll = async () => {
    try { await api.imessageMarkRead(); } catch {}
    setUnread(0);
    setItems((xs) => xs.map((n) => ({ ...n, read: true })));
  };
  const goMessages = async () => { setOpen(false); await markAll(); onOpen?.(); };
  const manage = () => { setOpen(false); window.dispatchEvent(new CustomEvent("open-imessage")); };

  const offline = conn && conn.connected && !conn.live;
  const liveDevice = (conn?.devices || []).find((d) => d.live);

  return (
    <div className="relative">
      <button data-testid="notif-bell" title="iMessage notifications & status" onClick={() => setOpen((v) => !v)}
        className={`relative w-9 h-9 grid place-items-center rounded-xl bg-white/[0.04] border transition-colors ${offline ? "border-amber-400/40" : "border-white/10 hover:border-cyan-400/40"}`}>
        <IMessageLogo size={18} className={offline ? "opacity-50" : importing ? "" : ""} />
        {importing ? (
          <span data-testid="notif-importing-dot" className="absolute -top-1.5 -right-1.5 w-[17px] h-[17px] rounded-full bg-cyan-500/90 grid place-items-center shadow-[0_0_8px_rgba(34,211,238,0.6)]" title="Importing your messages…">
            <Loader2 size={11} className="text-white animate-spin" />
          </span>
        ) : unread > 0 ? (
          <span data-testid="notif-badge" className="absolute -top-1.5 -right-1.5 min-w-[17px] h-[17px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center shadow-[0_0_8px_rgba(244,63,94,0.6)]">
            {unread > 9 ? "9+" : unread}
          </span>
        ) : offline ? (
          <span data-testid="notif-offline-dot" className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-400 border border-[#070a0f]" title="iMessage offline" />
        ) : conn?.live ? (
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-400 border border-[#070a0f]" title="iMessage live" />
        ) : null}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="notif-menu" className="absolute right-0 top-12 z-50 w-80 rounded-2xl bg-[#060a11] backdrop-blur-2xl border border-cyan-400/30 shadow-[0_24px_70px_rgba(0,0,0,0.85)] ring-1 ring-black/40 overflow-hidden">
            {/* Connection status strip */}
            <div data-testid="notif-conn-status" className="px-3 py-2.5 border-b border-white/10 bg-white/[0.02]">
              <div className="flex items-center gap-2">
                <span className={`inline-flex h-2 w-2 rounded-full shrink-0 ${conn?.live ? "bg-emerald-400" : conn?.connected ? "bg-amber-400" : "bg-zinc-600"}`} />
                <span className={`text-[12px] font-semibold ${conn?.live ? "text-emerald-300" : conn?.connected ? "text-amber-300" : "text-zinc-400"}`}>
                  {conn?.live ? `iMessage live${liveDevice ? ` · ${liveDevice.name}` : ""}` : conn?.connected ? "iMessage offline" : "iMessage not connected"}
                </span>
                <button data-testid="notif-manage-imessage" onClick={manage} className="ml-auto flex items-center gap-1 text-[11px] text-cyan-300 hover:text-cyan-200">
                  <Settings2 size={12} /> Manage
                </button>
              </div>
              {offline && (
                <p className="text-[11px] text-amber-200/70 mt-1 flex items-center gap-1">
                  <Laptop size={11} /> No Mac is checking in. Start the bridge on the Mac you're using.
                </p>
              )}
            </div>

            <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
              <span className="flex items-center gap-2 text-xs font-tactical font-bold uppercase tracking-widest text-cyan-200"><MessageSquare size={13} /> New messages</span>
              {unread > 0 && (
                <button data-testid="notif-mark-read" onClick={markAll} className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-cyan-200">
                  <Check size={12} /> Mark all read
                </button>
              )}
            </div>
            <div className="max-h-[300px] overflow-y-auto thin-scroll">
              {importing && (
                <div data-testid="notif-importing-banner" className="px-3 py-3 border-b border-cyan-400/15 bg-cyan-500/[0.06]">
                  <div className="flex items-center gap-2.5">
                    <Loader2 size={15} className="text-cyan-300 animate-spin shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[12px] font-semibold text-cyan-200">Importing your messages…</p>
                      <p className="text-[11px] text-cyan-200/60">
                        You've got a lot of history — {fmtEta(eta)}.
                        {conn?.messages ? ` ${conn.messages.toLocaleString()}${conn?.total_expected ? ` of ~${conn.total_expected.toLocaleString()}` : ""} so far.` : ""}
                      </p>
                    </div>
                  </div>
                  {conn?.total_expected > 0 && (
                    <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-cyan-400 rounded-full transition-all duration-700"
                        style={{ width: `${Math.min(99, Math.round((conn.messages / conn.total_expected) * 100))}%` }} />
                    </div>
                  )}
                  <button data-testid="notif-connect-contacts" onClick={manage}
                    className="mt-2.5 w-full flex items-center gap-2 rounded-lg border border-cyan-400/25 bg-cyan-500/10 hover:bg-cyan-500/20 px-2.5 py-2 text-left transition-colors">
                    <UserPlus size={14} className="text-cyan-300 shrink-0" />
                    <span className="text-[11px] text-cyan-100 leading-tight">While this loads, <strong>connect your contacts</strong> so names show instead of phone numbers.</span>
                  </button>
                </div>
              )}
              {items.length === 0 ? (
                !importing && (
                  <div data-testid="notif-empty" className="px-3 py-8 text-center text-[12px] text-zinc-500">
                    No new messages yet.<br />New iMessages show up here live.
                  </div>
                )
              ) : items.map((n) => (
                <button key={n.id} data-testid="notif-item" onClick={goMessages}
                  className={`w-full text-left px-3 py-2.5 border-b border-white/5 hover:bg-white/[0.04] transition-colors ${n.read ? "opacity-60" : ""}`}>
                  <div className="flex items-center gap-2">
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" />}
                    <span className="text-[13px] font-semibold text-zinc-100 truncate flex-1">{n.name}</span>
                    <span className="text-[10px] text-zinc-500 shrink-0">{timeAgo(n.created_at)}</span>
                  </div>
                  <p className="text-[12px] text-zinc-400 line-clamp-2 mt-0.5">{n.preview}</p>
                </button>
              ))}
            </div>
            <button data-testid="notif-view-all" onClick={goMessages} className="w-full py-2.5 text-[12px] font-semibold text-cyan-300 hover:bg-cyan-500/10 border-t border-white/10">
              Open Messages
            </button>
          </div>
        </>
      )}
    </div>
  );
}
